import React, { useEffect, useState } from "react";
import { apiService } from "../../../components/Config/api.service";
import { MdOutlineUploadFile } from "react-icons/md";
import { FaArrowLeft, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import EmployeeSelect from "../../../components/Formulaire/EmployeeSelect";
import { FiTrash2 } from 'react-icons/fi';
import ActionDropdown from '../../../components/Utils/ActionDropdown';
import Modal from '../../../components/Utils/ModalShow';
import AmountInput from "../../../components/Utils/AmountInput";

const NoteFrais = ({ id, setErrorMessage, setSuccessMessage, closeEdit }) => {
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isAccountSectionExpanded, setIsAccountSectionExpanded] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        amount: "",
        date: "",
        description: "",
        account_debit: '',
        account_credit: '',
        not_computed_in_net: false,
    });
    const [existingDocument, setExistingDocument] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const noteResponse = id ? await apiService.getElement(`/note/${id}`) : null;

                if (noteResponse) {
                    const note = noteResponse.data.note;
                    setFormData({
                        name: note.name,
                        amount: note.amount,
                        date: note.date,
                        description: note.description || "",
                    });
                    setSelectedEmployee({
                        value: note.employee_id,
                        label: note.employee_name,
                    });
                    setUploadedFile(null);
                    setExistingDocument(note.has_document);
                }
            } catch (error) {
                setErrorMessage("Erreur lors du chargement des données.");
            }
        };

        fetchData();
    }, [id, setErrorMessage]);

    // Handle input changes
    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
    };

    const handleMontantChange = (newValue) => {
        setFormData((prev) => ({ ...prev, amount: newValue }));
    };

    // Handle file upload
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && file.size > 25 * 1024 * 1024) {
            setErrorMessage("Le fichier dépasse la taille maximale autorisée de 25 Mo.");
            return;
        }
        setUploadedFile(file);
    };

    // Validate form data
    const validateForm = () => {
        if (!selectedEmployee) return "Veuillez sélectionner un employé.";
        if (!formData.name) return "Veuillez entrer un libellé pour la note de frais.";
        if (!formData.amount || parseFloat(formData.amount) <= 0) return "Veuillez entrer un montant valide.";
        if (!formData.date) return "Veuillez sélectionner une date.";
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const error = validateForm();
        if (error) {
            setErrorMessage(error);
            return;
        }

        setIsSubmitting(true);

        try {
            let payload;
            let sendFile = false;

            if (uploadedFile) {
                // Utilisation de FormData pour multipart
                payload = new FormData();
                payload.append("name", formData.name);
                payload.append("amount", formData.amount);
                payload.append("date", formData.date);
                payload.append("description", formData.description);
                payload.append("employee_id", selectedEmployee.value);
                payload.append("supporting_document", uploadedFile);
                sendFile = true;
            } else {
                // Envoi classique JSON
                payload = {
                    ...formData,
                    employee_id: selectedEmployee.value,
                };
            }

            if (id) {
                await apiService.updateElementById("/note", id, payload, sendFile);
            } else {
                await apiService.saveElement("/note", payload, sendFile);
                setFormData({ name: "", amount: "", date: "", description: "" });
                setSelectedEmployee(null);
                setUploadedFile(null);
            }
            closeEdit();
        } catch (error) {
            setErrorMessage("Erreur lors de l'enregistrement de la note de frais.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const downloadExistingDocument = async () => {
        try {
            const response = await apiService.getBlobFile(`/note/${id}/download`);
            const blob = new Blob([response.data], { type: response.headers["content-type"] });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "document"; // ou le nom du fichier que tu veux
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setErrorMessage("Erreur lors du téléchargement du document.");
        }
    };

    const handleArchive = async () => {
        setIsArchiveModalOpen(false);
        setIsSubmitting(true);
        try {
            await apiService.updateElementById(`/note`, id, { active: false });
            setSuccessMessage && setSuccessMessage('Archivage réussi');
            closeEdit();
        } catch (error) {
            setErrorMessage("Erreur lors de l'archivage de la note de frais.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const dropdownActions = [
        {
            label: 'Archiver',
            onClick: () => setIsArchiveModalOpen(true),
            disabled: isSubmitting || !id,
            color: 'text-red-500',
            icon: <FiTrash2 size={16} />
        }
    ];

    return (
        <>
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className='flex justify-between items-center mb-6'>
                <h1 className="text-2xl font-bold flex items-center">
                    <small className="mr-4 text-gray-400 cursor-pointer" onClick={closeEdit}><FaArrowLeft /></small>
                    Note de frais
                </h1>
                <div>
                    <button
                        type="button"
                        className="bg-orange-200 text-black rounded-full btn mx-2"
                        onClick={closeEdit}
                    >
                        Annuler
                    </button>
                    <button
                        type="submit"
                        className="bg-[--color-primary] text-white hover:bg-purple-500 rounded-full btn"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <ClipLoader color='white' size={20} /> : 'Enregistrer'}
                    </button>
                    <ActionDropdown actions={dropdownActions} />
                </div>
            </div>

            {/* Section Informations générales */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h3 className="text-lg font-semibold text-gray-800">Informations générales</h3>
                    <p className="text-sm text-gray-600 mt-1">Détails de base de la note de frais</p>
                </div>
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="employee" className="block text-sm font-medium text-gray-700">
                                Employé *
                            </label>
                            <EmployeeSelect
                                value={selectedEmployee}
                                onChange={setSelectedEmployee}
                                isDisabled={isSubmitting}
                                placeholder="Choisir un employé"
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                                Date *
                            </label>
                            <input
                                type="date"
                                id="date"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                value={formData.date}
                                onChange={handleInputChange}
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Motif *
                            </label>
                            <input
                                type="text"
                                id="name"
                                placeholder="Description de la note de frais"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                value={formData.name}
                                onChange={handleInputChange}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                                Montant *
                            </label>
                            <AmountInput
                                value={formData.amount}
                                onChange={handleMontantChange}
                                placeholder="Montant de la note de frais"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            Description (facultatif)
                        </label>
                        <textarea
                            id="description"
                            placeholder="Description détaillée"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Section Document justificatif */}
                    <div className="space-y-2">
                        <label htmlFor="justificatif" className="block text-sm font-medium text-gray-700">
                            Document Justificatif
                        </label>
                        <div className="flex gap-4 items-center justify-center border-2 border-gray-300 border-dashed rounded-lg p-6 hover:border-indigo-300 transition-colors duration-200">
                            <div className="bg-purple-100 rounded-full p-3">
                                <MdOutlineUploadFile className="text-3xl text-[--color-primary]" />
                            </div>
                            <div className="text-center">
                                <label
                                    htmlFor="file-upload"
                                    className="cursor-pointer font-semibold text-[--color-primary] hover:text-purple-700 transition-colors duration-200"
                                >
                                    {uploadedFile ? "Remplacer le document" : "Cliquez pour télécharger"}
                                </label>
                                <input
                                    id="file-upload"
                                    type="file"
                                    className="hidden"
                                    onChange={handleFileChange}
                                    disabled={isSubmitting}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                />
                                <p className="text-sm text-gray-500 mt-1">ou faites glisser et déposez</p>
                                <p className="text-xs text-gray-400 mt-1">(Taille max. du fichier : 25 Mo)</p>
                            </div>
                        </div>
                        {uploadedFile && (
                            <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex-1">
                                    <p className="text-sm text-green-800 font-medium">Fichier sélectionné :</p>
                                    <p className="text-sm text-green-600">{uploadedFile.name}</p>
                                </div>
                            </div>
                        )}
                        {existingDocument && !uploadedFile && (
                            <div className="flex items-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex-1">
                                    <p className="text-sm text-blue-800 font-medium">Document existant disponible</p>
                                    <button
                                        type="button"
                                        onClick={downloadExistingDocument}
                                        className="text-sm text-blue-600 hover:text-blue-800 underline mt-1"
                                    >
                                        Télécharger le justificatif existant
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Section Configuration comptable */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div 
                    className="border-b border-gray-200 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => setIsAccountSectionExpanded(!isAccountSectionExpanded)}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-800">Configuration comptable</h3>
                            <p className="text-sm text-gray-600 mt-1">Paramètres comptables et calculs</p>
                        </div>
                        <div className="text-gray-400">
                            {isAccountSectionExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                    </div>
                </div>
                {isAccountSectionExpanded && (
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Compte de Débit
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                    name="account_debit"
                                    value={formData.account_debit || ''}
                                    onChange={e => setFormData(prev => ({ ...prev, account_debit: e.target.value }))}
                                    placeholder="Compte de débit"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Compte de Crédit
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                                    name="account_credit"
                                    value={formData.account_credit || ''}
                                    onChange={e => setFormData(prev => ({ ...prev, account_credit: e.target.value }))}
                                    placeholder="Compte de crédit"
                                />
                            </div>
                        </div>
                        
                        {/* Séparateur visuel */}
                        <div className="border-t border-gray-200 pt-6">
                            <h4 className="text-md font-medium text-gray-800 mb-4">Options de calcul</h4>
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div>
                                    <h5 className="font-medium text-gray-800">Non Computé dans le Net</h5>
                                    <p className="text-sm text-gray-600 mt-1">Cette note de frais ne sera pas incluse dans le calcul du salaire net</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        name="not_computed_in_net"
                                        checked={!!formData.not_computed_in_net}
                                        onChange={e => setFormData(prev => ({ ...prev, not_computed_in_net: e.target.checked }))}
                                    />
                                    <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </form>

        <Modal
            isOpen={isArchiveModalOpen}
            onClose={() => setIsArchiveModalOpen(false)}
            title="Confirmation d'archivage"
            footer={
                <>
                    <button className="btn" onClick={() => setIsArchiveModalOpen(false)}>Annuler</button>
                    <button className="btn btn-error" onClick={handleArchive}>Confirmer</button>
                </>
            }
        >
            <p>Êtes-vous sûr de vouloir archiver cet élément ?</p>
        </Modal>
        </>
    );
};

export default NoteFrais;